import React, { ReactChild, useEffect, useState } from "react";
import { Task, ViewMode } from "../../types/public-types";
import { addToDate } from "../../helpers/date-helper";
import styles from "./grid.module.css";

export type GridBodyProps = {
  tasks: Task[];
  dates: Date[];
  holidays?: Date[];
  projectStartDate?: Date;
  projectEndDate?: Date;
  nonWorkingDays?: number[];
  svgWidth: number;
  rowHeight: number;
  columnWidth: number;
  todayColor: string;
  rtl: boolean;
  viewMode: ViewMode;
  startEndIndicater?:boolean
  selectedTasks?:Task
};

export const GridBodyComponent: React.FC<GridBodyProps> = ({
  tasks,
  dates,
  holidays,
  nonWorkingDays,
  rowHeight,
  svgWidth,
  columnWidth,
  rtl,
  viewMode,
  projectStartDate,
  projectEndDate,
  startEndIndicater,
  // selectedTasks
}) => {
  function dateFormater(inputDate: Date): string {
    const date = new Date(inputDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${day.toString().padStart(2, "0")}/${month
      .toString()
      .padStart(2, "0")}/${year}`;
  }
  let y = 0;
  const gridRows: ReactChild[] = [];
  const rowLines: ReactChild[] = [
    <line
      key="RowLineFirst"
      x="0"
      y1={0}
      x2={svgWidth}
      y2={0}
      className={styles.gridRowLine}
    />,
  ];
  const divHeight = document.getElementById("div")?.offsetHeight || 0;
  let totalTaskHeight = 0;
  
  // Render the task rows
  for (const task of tasks) {
    if (!tasks.find((t) => t.id == task.project)?.hideChildren) {
      gridRows.push(
        <rect
          key={"Row" + task.id}
          id={task.id}
          x="0"
          y={y}
          data-TaskRow = {task.id}
          width={svgWidth}
          height={rowHeight}
          className={styles.gridRow }
        />
      );
      rowLines.push(
        <line
          key={"RowLine" + task.id}
          x="0"
          id={task.id}
          y1={y + rowHeight}
          x2={svgWidth}
          y2={y + rowHeight}
          className={styles.gridRowLine}
        />
      );
      y += rowHeight;
      totalTaskHeight += rowHeight;
    }
  }
  // Calculate the remaining height
  const remainingHeight = divHeight - totalTaskHeight;
  
  // Determine how many additional rows are needed to fill the blank space
  const additionalRows = Math.ceil(remainingHeight / rowHeight);
  // Add the additional rows
  for (let i = 0; i < additionalRows; i++) {
    gridRows.push(
      <rect
        key={"ExtraRow" + i}
        x="0"
        y={y}
        width={svgWidth}
        height={rowHeight}
        className={styles.gridRow}
      />
    );
    rowLines.push(
      <line
        key={"ExtraRowLine" + i}
        x="0"
        y1={y + rowHeight}
        x2={svgWidth}
        y2={y + rowHeight}
        className={styles.gridRowLine}
      />
    );
    y += rowHeight;
  }
  
  const holidayRects: ReactChild[] = [];
  const nonWorkingDayRects: ReactChild[] = [];
  const now = new Date();
  let tickX = 0;
  const ticks: ReactChild[] = [];
  let today: ReactChild = <rect />;

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    ticks.push(
      <line
        key={date?.getTime()}
        x1={tickX}
        y1={0}
        x2={tickX}
        y2={y}
        className={styles.gridTick}
      />
    );
    if (
      (i + 1 !== dates.length &&
        date?.getTime() < now?.getTime() &&
        dates[i + 1]?.getTime() >= now?.getTime()) ||
      (i !== 0 &&
        i + 1 === dates.length &&
        date?.getTime() < now?.getTime() &&
        addToDate(
          date,
          date?.getTime() - dates[i - 1]?.getTime(),
          "millisecond"
        )?.getTime() >= now?.getTime())
    ) {
      today = (
        <rect
          x={tickX}
          y={0}
          width={columnWidth}
          height={y}
          fill={"#299d9122"}
          className="bg-slate-300"
        />
      );
    }
    if (
      rtl &&
      i + 1 !== dates.length &&
      date?.getTime() >= now?.getTime() &&
      dates[i + 1]?.getTime() < now?.getTime()
    ) {
      today = (
        <rect
          x={tickX + columnWidth}
          y={0}
          width={columnWidth}
          height={y}
          fill={"#ff676722"}
        />
      );
    }
    if (holidays) {
      if (holidays.map((d) => dateFormater(d)).includes(dateFormater(date))) {
        holidayRects.push(
          <rect
            key={`holiday-${date?.getTime()}`}
            x={tickX}
            y={0}
            width={columnWidth}
            height={y}
            fill={"#78748611"} // Adjust the color as needed
            className={styles.holidayRect}
          />
        );
      }
    }
    if (nonWorkingDays) {
      if (nonWorkingDays.includes(date.getDay())) {
        nonWorkingDayRects.push(
          <rect
            key={`holiday-${date?.getTime()}`}
            x={tickX}
            y={0}
            width={columnWidth}
            height={y}
            fill={"#78748611"} // Adjust the color as needed
            className={styles.holidayRect}
          />
        );
      }
    }
    tickX += columnWidth;
  }

  const [startofProject, setStartofProject] = useState<ReactChild>(<rect />);
  const [endofProject, setEndofProject] = useState<ReactChild>(<rect />);

  useEffect(() => {
    updateProjectDates();
  }, [tasks, columnWidth, dates,projectEndDate,projectStartDate]);

  const updateProjectDates = () => {
    // let localStartOfProject: ReactChild = <rect />;
    // let localEndOfProject: ReactChild = <rect />;
    let tickX = 0;

    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];

      if (projectStartDate && startEndIndicater) {
        if (
          i + 1 !== dates.length &&
          date?.getTime() < projectStartDate?.getTime() &&
          dates[i + 1]?.getTime() >= projectStartDate?.getTime()
        ) {
          setStartofProject(
            <svg>
              <rect
                key={`projectStart-${date?.getTime()}`}
                x={tickX}
                y={0}
                width={2}
                height={y}
                fill={"#fab005"}
                className={styles.holidayRect}
              />
              {/* <g className={styles.projectStartFlag}>
                <rect
                  x={tickX -80}
                  y={0}
                  width={80}
                  height={20}
                  fill="#fab005"
                  className={styles.flagBackground}
                />
                <text
                  x={tickX -70}
                  y={15}
                  fill="#FFFFFF"
                  fontSize="12"
                  className={styles.projectStartText + " select-none"}
                >
                  Project Start
                </text>
              </g> */}
            </svg>
          );
        }
      }

      if (projectEndDate && startEndIndicater) {
        if (
          i + 1 !== dates.length &&
          date?.getTime() < projectEndDate?.getTime() &&
          dates[i + 1]?.getTime() >= projectEndDate?.getTime()
        ) {
          setEndofProject(
            <svg>
              <rect
                key={`projectEnd-${date?.getTime()}`}
                x={tickX}
                y={0}
                width={2}
                height={y}
                fill={"#fab005"}
                className={styles.holidayRect}
              />
              {/* <g className={styles.projectEndFlag}>
                <rect
                  x={tickX + 1}
                  y={0}
                  width={80}
                  height={20}
                  fill="#fab005"
                  className={styles.flagBackground}
                />
                <text
                  x={tickX + 10}
                  y={15}
                  fill="#FFFFFF"
                  fontSize="12"
                  className={styles.projectStartText + " select-none"}
                >
                  Project End
                </text>
              </g> */}
            </svg>
          );
        }
      }

      tickX += columnWidth;
    }


  };
// useEffect(() => {
//   // Reset previous highlights (optional but recommended)
//   const allRects = document.querySelectorAll(`rect[data-TaskRow]`);
//   allRects.forEach((r) => r.setAttribute("fill", "#fff")); // default fill or zebra logic
//   // Highlight selected task
//   if (selectedTasks?.id) {
//     const rect = document.querySelector(`rect[data-TaskRow="${selectedTasks.id}"]`) as SVGRectElement;
//     if (rect) {
//       rect.setAttribute("fill", "#299d913d"); // Tailwind primary-300 equivalent
//     }
//   }
// }, [selectedTasks]);
// useEffect(() => {
//   const allRects = document.querySelectorAll(`rect[data-TaskRow]`);
//   allRects.forEach((r) => r.setAttribute("fill", "#fff")); // default fill or zebra logic

// }, [dates]);

  
  
  return (
    <g className="gridBody">
      <g className="rows">{gridRows}</g>
      <g className="rowLines">{rowLines}</g>
      <g className="ticks">{ticks}</g>
      <g className="today">{today}</g>
      <g className="projectStart">{startofProject}</g>
      <g className="projectEnd">{endofProject}</g>
      {viewMode == ViewMode.Day && holidayRects} {viewMode == ViewMode.Day &&  nonWorkingDayRects}
    </g>
  );
};
export const GridBody = React.memo(GridBodyComponent);